import type { RequireAtLeastOne } from 'type-fest'

import type {
  FeedId,
  OrganizationId,
  OrganizationType,
  TeamId,
  UserId,
} from '@mntn-dev/domain-types'
import { env } from '@mntn-dev/env'
import {
  FeedChannel,
  OrganizationChannel,
  OrganizationTypeChannel,
  type RealtimeSendFunction,
  TeamChannel,
  UserChannel,
} from '@mntn-dev/realtime-updates-types'

import { asArray, defined, uniq } from '@mntn-dev/utilities'
import type { SingleOrArray } from '@mntn-dev/utility-types'
import { innerSendMessage, requestToken } from './adapted-services.ts'
import { realtimeUpdateLogger } from './realtime-update-logger.ts'
import type { RealtimeSchema } from './schemas.ts'

export type ChannelIds = RequireAtLeastOne<{
  organizationType: SingleOrArray<OrganizationType>
  organizationId: SingleOrArray<OrganizationId>
  teamId: SingleOrArray<TeamId>
  userId: SingleOrArray<UserId>
  feedId: SingleOrArray<FeedId>
}>

const sendMessage: RealtimeSendFunction<RealtimeSchema, ChannelIds> = (
  { organizationType, organizationId, teamId, userId, feedId },
  type,
  message
) => {
  if (env.DISABLE_ASYNC_MESSAGES === 'true') {
    return Promise.resolve(0)
  }

  const channels = uniq([
    OrganizationTypeChannel('internal'), // Internal users get all messages
    ...defined(asArray(organizationType)).map(OrganizationTypeChannel),
    ...defined(asArray(organizationId)).map(OrganizationChannel),
    ...defined(asArray(teamId)).map(TeamChannel),
    ...defined(asArray(userId)).map(UserChannel),
    ...defined(asArray(feedId)).map(FeedChannel),
  ])

  realtimeUpdateLogger.debug('Sending realtime message --->', {
    channels,
    type,
    message,
  })

  return innerSendMessage(channels, type, message)
}

const sendTrpcRouterUpdates = (
  options: {
    channels: ChannelIds
  } & RealtimeSchema['trpc.router.updates']
) => {
  const { channels, refresh, originId } = options
  return sendMessage(channels, 'trpc.router.updates', { originId, refresh })
}

export const realtimeServerService = {
  requestToken,
  sendMessage,
  sendTrpcRouterUpdates,
}
